import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../client/config';

// =============================================================================
// Auth gate (W11 login screen).
//
// On boot the Admin SPA probes GET /api/auth/me to decide whether to render
// the app or the Login screen:
//
//   • 200 (a user)        → authed   → render the app shell.
//   • 401 (no session)    → guest    → render Login (email/password + Google).
//   • 428 (password change) → password-change → render ChangePassword.
//   • server/network failure → checking + serverError → keep the app closed
//                            while retrying; do not misrepresent an outage as
//                            a logout.
//
// AUTH_ENABLED=false (local): the server answers /api/auth/me with 200 (a
// default/dev user), so this resolves to `authed` and the app is never
// blocked — only an explicit 401 gates. That keeps auth-off local dev working
// without a special case here.
//
// `recheck` lets the Login screen re-probe after a successful password login
// without a full page reload, flipping the gate to `authed`.
// =============================================================================

export type AuthGate = 'checking' | 'authed' | 'guest' | 'password-change';
export const AUTH_GATE_UNAVAILABLE_MESSAGE = '서버에 연결할 수 없습니다. 다시 시도해 주세요.';
const AUTH_GATE_TIMEOUT_MS = 10_000;

export function useAuthGate(): { gate: AuthGate; serverError: string | null; recheck: () => void } {
  const [gate, setGate] = useState<AuthGate>('checking');
  const [serverError, setServerError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retryAttempt = useRef(0);

  const recheck = useCallback(() => {
    retryAttempt.current = 0;
    setGate('checking');
    setServerError(null);
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    // Hide mounted editors immediately while a permission-triggered reload
    // replaces the JavaScript context and cancels its outstanding requests.
    const reset = () => setGate('checking');
    window.addEventListener('bricklit:authorization-reset', reset);
    return () => window.removeEventListener('bricklit:authorization-reset', reset);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    let timedOut = false;
    const requestTimeout = setTimeout(() => {
      timedOut = true;
      ac.abort();
    }, AUTH_GATE_TIMEOUT_MS);
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const retry = () => {
      const delay = Math.min(1_000 * 2 ** retryAttempt.current, 30_000);
      retryAttempt.current += 1;
      retryTimer = setTimeout(() => setNonce((n) => n + 1), delay);
    };
    fetch(`${API_BASE}/api/auth/me`, { signal: ac.signal })
      .then(async (r) => {
        if (ac.signal.aborted) return;
        if (r.status === 401 || r.status === 403) {
          retryAttempt.current = 0;
          setServerError(null);
          setGate('guest');
          return;
        }
        if (r.status === 428) {
          retryAttempt.current = 0;
          setServerError(null);
          setGate('password-change');
          return;
        }
        if (!r.ok) {
          setGate('checking');
          setServerError(AUTH_GATE_UNAVAILABLE_MESSAGE);
          retry();
          return;
        }
        const me = await r.json() as { mustChangePassword?: boolean };
        if (!ac.signal.aborted) {
          retryAttempt.current = 0;
          setServerError(null);
          setGate(me.mustChangePassword ? 'password-change' : 'authed');
        }
      })
      .catch(() => {
        if (!ac.signal.aborted || timedOut) {
          setGate('checking');
          setServerError(AUTH_GATE_UNAVAILABLE_MESSAGE);
          retry();
        }
      })
      .finally(() => clearTimeout(requestTimeout));
    return () => {
      ac.abort();
      clearTimeout(requestTimeout);
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [nonce]);

  return { gate, serverError, recheck };
}
